import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiLock, FiShield, FiUnlock } from "react-icons/fi";

import apiService from "../api/apiService";
import styles from "./AdminPinGate.module.css";

/**
 * Bramka PIN-u przed panelem administratora.
 *
 * Ekran jest tylko wygodą — o dostępie rozstrzyga SERWER: kontrolery administracyjne mają
 * własny filtr i odbijają żądanie kodem 423, gdy panel nie jest odblokowany. Dzięki temu
 * ominięcie tego komponentu (wpisanie adresu, wywołanie API) niczego nie otwiera.
 *
 * Odblokowanie żyje po stronie serwera przez ustalony czas i dotyczy WYŁĄCZNIE panelu —
 * wygaśnięcie nie wylogowuje z reszty aplikacji, więc obsługa nie traci pracy w przepustkach.
 */
const AdminPinGate = ({ children }) => {
    const [state, setState] = useState(null);
    const [pin, setPin] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    const loadStatus = useCallback(async () => {
        try {
            const res = await apiService.getAdminAccessStatus();
            setState(res.data);
        } catch {
            // Brak odpowiedzi traktujemy jak zamknięty panel — nie otwieramy go „w razie wątpliwości".
            setState({ unlocked: false, pinSet: false, bypass: false });
        }
    }, []);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    useEffect(() => {
        if (state && !state.unlocked && !state.bypass) {
            inputRef.current?.focus();
        }
    }, [state]);

    const submit = async (event) => {
        event.preventDefault();
        if (busy || pin.length < 4) return;
        setBusy(true);
        setError("");
        try {
            const res = await apiService.unlockAdminAccess(pin);
            setState((prev) => ({ ...prev, unlocked: true, unlockedUntil: res.data?.unlockedUntil }));
            setPin("");
        } catch (err) {
            setError(err?.response?.data?.message || "Nie udało się odblokować panelu.");
            setPin("");
            inputRef.current?.focus();
        } finally {
            setBusy(false);
        }
    };

    if (!state) {
        return null;
    }

    if (state.unlocked || state.bypass) {
        return children;
    }

    const noPin = !state.pinSet;

    return (
        <div className={styles.wrap}>
            <div className={styles.card}>
                <span className={styles.icon}>
                    <FiShield size={26} />
                </span>
                <h1 className={styles.title}>Panel administratora</h1>
                <p className={styles.lead}>
                    {noPin
                        ? "Wejście do panelu wymaga PIN-u, którego nie masz jeszcze nadanego."
                        : "Wejście do panelu wymaga PIN-u. To dodatkowe zabezpieczenie konfiguracji, kartotek i dzienników."}
                </p>

                {noPin ? (
                    <p className={styles.note}>
                        <FiLock size={13} /> PIN nadaje administrator w module „Użytkownicy".
                        Zgłoś się po niego osobiście — nie jest wysyłany żadnym kanałem.
                    </p>
                ) : (
                    <form className={styles.form} onSubmit={submit}>
                        <label className={styles.label} htmlFor="admin-pin">
                            PIN
                        </label>
                        <input
                            id="admin-pin"
                            ref={inputRef}
                            className={styles.input}
                            type="password"
                            inputMode="numeric"
                            autoComplete="off"
                            value={pin}
                            maxLength={12}
                            placeholder="••••"
                            onChange={(event) => setPin(event.target.value.replace(/\D/g, ""))}
                        />
                        {error && <p className={styles.error}>{error}</p>}
                        <button
                            type="submit"
                            className={styles.button}
                            disabled={busy || pin.length < 4}
                        >
                            <FiUnlock /> {busy ? "Sprawdzam…" : "Odblokuj panel"}
                        </button>
                        {state.unlockMinutes && (
                            <p className={styles.note}>
                                Po odblokowaniu panel zostaje otwarty przez {state.unlockMinutes} minut.
                            </p>
                        )}
                    </form>
                )}
            </div>
        </div>
    );
};

export default AdminPinGate;
